import type { TxSummary } from '@apm/contracts';
import { useEffect, useRef, useState } from 'react';
import { usePixelRatio } from '../hooks/usePixelRatio';
import { streamStore } from '../store/stream';
import { DEFAULT_FILTER, type RowFilter } from './filter';
import { shouldRedraw } from './redraw';
import { XViewRenderer } from './renderer';
import {
  autoMaxMs,
  DEFAULT_MAX_MS,
  DEFAULT_MIN_MS,
  DEFAULT_PADDING,
  type Plot,
  pickNearest,
  type Rect,
  type RectSelection,
  rowsInRect,
} from './scale';

/** 클릭 판정 반경(CSS 픽셀) */
const PICK_RADIUS = 8;
/** 이보다 짧게 끌면 드래그가 아니라 클릭으로 본다 */
const DRAG_THRESHOLD = 4;

export interface XViewStats {
  points: number;
  frameMs: number;
  /** 지금 세로축 상한. 화면이 "60초까지" 같은 안내를 낼 때 쓴다 */
  maxMs: number;
}

/**
 * 그림판 한 장. 렌더러는 React 밖에 있고 여기서는 크기·프레임·클릭·드래그만 다룬다.
 *
 * 점 배열은 상태로 올리지 않는다. `streamStore.getRows()`를 프레임마다 직접 읽는다.
 * 수만 건짜리 배열을 React 상태에 넣으면 그 자체가 프레임을 넘긴다.
 * 드래그 사각형도 ref에 두고 렌더러에 넘긴다. 끄는 동안 다시 그리기가 일어나면 안 된다.
 *
 * 거르기도 같은 이유로 ref에 둔다. 값이 바뀌어도 다시 붙이지 않고 다음 프레임부터 반영된다.
 *
 * 23-A회차에 **과거 보기**가 붙었다(조정자 제안 8). `rows`·`window`를 주면 실시간 스토어가
 * 아니라 그것을 그린다. 점 고르기와 끌어 고르기도 같은 목록을 본다 — 두 곳이 다른 목록을
 * 보면 "누른 점과 다른 요청이 열리는" 일이 생긴다.
 */
export function XView({
  onPick,
  onSelectRect,
  onStats,
  filter = DEFAULT_FILTER,
  rows,
  window: frozen,
}: {
  onPick: (row: TxSummary | undefined) => void;
  onSelectRect: (selection: RectSelection | undefined) => void;
  onStats?: (stats: XViewStats) => void;
  filter?: RowFilter;
  /** 그릴 목록. 없으면 실시간 스토어를 프레임마다 읽는다 (23-A회차 과거 보기) */
  rows?: readonly TxSummary[];
  /** 가로축을 이 기간에 못 박는다. 없으면 오른쪽 끝이 지금이고 계속 흐른다 */
  window?: { now: number; windowMs: number };
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const plotRef = useRef<Plot>({
    width: 0,
    height: 0,
    padding: DEFAULT_PADDING,
    now: Date.now(),
    windowMs: streamStore.windowMs,
    minMs: DEFAULT_MIN_MS,
    maxMs: DEFAULT_MAX_MS,
  });
  const statsRef = useRef(onStats);
  statsRef.current = onStats;
  const filterRef = useRef(filter);
  filterRef.current = filter;
  // 과거 보기의 목록과 기간. 바뀌어도 캔버스를 다시 붙이지 않고 다음 프레임부터 반영한다
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const windowRef = useRef(frozen);
  windowRef.current = frozen;
  /** 지금 그리는 목록. 고르기와 그리기가 같은 것을 봐야 한다 */
  const visible = () => rowsRef.current ?? streamStore.getRows();
  /** 끄는 중인 사각형. 없으면 드래그 중이 아니다 */
  const dragRef = useRef<Rect | undefined>(undefined);
  /** 마지막 그림 뒤로 그림에 쓰이는 것이 바뀌었다. 다음 프레임에 다시 그린다 (44회차) */
  const dirtyRef = useRef(true);
  const rendererRef = useRef<XViewRenderer | undefined>(undefined);
  const ratio = usePixelRatio();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new XViewRenderer(canvas);
    rendererRef.current = renderer;
    let frame = 0;
    let lastDraw = Number.NEGATIVE_INFINITY;
    /** 마지막으로 본 그림 재료. 과거 보기의 기간은 렌더마다 새 객체라 값으로 견준다 */
    let seen = {
      rows: rowsRef.current,
      filter: filterRef.current,
      now: windowRef.current?.now,
      windowMs: windowRef.current?.windowMs,
      txVersion: streamStore.getState().txVersion,
    };

    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box) return;
      plotRef.current.width = box.width;
      plotRef.current.height = box.height;
      renderer.resize(box.width, box.height, window.devicePixelRatio || 1);
      dirtyRef.current = true;
    });
    observer.observe(canvas);

    // 가로축이 시각이라 실시간 보기는 점이 안 들어와도 눈금이 흘러야 한다. 다만 매 프레임 점 전부를
    // 다시 찍으면 3만 점에서 한 프레임을 다 쓴다(44회차, `redraw.ts`). 바뀐 것이 있거나 끄는 중이면
    // 곧바로, 실시간이면 그 밖에도 250ms 마다 그린다. 누르기 판정은 **그린 때의 좌표**(`plotRef`)를
    // 쓰므로 보이는 점과 고르는 점이 어긋나지 않는다
    const tick = () => {
      const frozen = windowRef.current;
      const txVersion = streamStore.getState().txVersion;
      if (
        rowsRef.current !== seen.rows ||
        filterRef.current !== seen.filter ||
        frozen?.now !== seen.now ||
        frozen?.windowMs !== seen.windowMs ||
        (rowsRef.current === undefined && txVersion !== seen.txVersion)
      ) {
        dirtyRef.current = true;
        seen = {
          rows: rowsRef.current,
          filter: filterRef.current,
          now: frozen?.now,
          windowMs: frozen?.windowMs,
          txVersion,
        };
      }
      const at = performance.now();
      if (
        shouldRedraw({
          dirty: dirtyRef.current,
          dragging: dragRef.current !== undefined,
          live: frozen === undefined,
          sinceLastMs: at - lastDraw,
        })
      ) {
        dirtyRef.current = false;
        lastDraw = at;
        plotRef.current.now = frozen?.now ?? Date.now();
        plotRef.current.windowMs = frozen?.windowMs ?? streamStore.windowMs;
        const result = renderer.draw(
          rowsRef.current ?? streamStore.getRows(),
          plotRef.current,
          dragRef.current,
          filterRef.current,
        );
        // 세로축은 이번에 본 최댓값으로 다음 그림에서 늘어난다 (후보 #3). 바뀌었으면 곧바로 다시 그린다
        const maxMs = autoMaxMs(result.maxDurationMs);
        if (maxMs !== plotRef.current.maxMs) dirtyRef.current = true;
        plotRef.current.maxMs = maxMs;
        statsRef.current?.({
          points: result.drawn,
          frameMs: result.elapsedMs,
          maxMs: plotRef.current.maxMs,
        });
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      rendererRef.current = undefined;
    };
  }, []);

  // 배율만 바뀌면(배율이 다른 모니터로 창을 옮김) 크기 감시가 부르지 않는다. 캔버스 해상도를
  // 새 배율로 다시 맞춘다 (44회차). 좌표는 CSS 픽셀이라 누르기 판정은 그대로다
  useEffect(() => {
    const renderer = rendererRef.current;
    const plot = plotRef.current;
    if (renderer === undefined || plot.width === 0) return;
    renderer.resize(plot.width, plot.height, ratio);
    dirtyRef.current = true;
  }, [ratio]);

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    const canvas = event.currentTarget;
    const bounds = canvas.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    dragRef.current = { x0: x, y0: y, x1: x, y1: y };
    // 그림판 밖으로 나가도 끝까지 따라오게 한다. 못 잡아도 드래그는 계속된다
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      // 포인터를 잡을 수 없는 환경이면 그냥 넘어간다
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    drag.x1 = event.clientX - bounds.left;
    drag.y1 = event.clientY - bounds.top;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    dragRef.current = undefined;
    if (!drag) return;
    // 끄던 사각형을 지우려면 한 번 더 그려야 한다
    dirtyRef.current = true;
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // 잡지 못했으면 풀 것도 없다
    }

    const shown = visible();
    const plot = plotRef.current;
    const current = filterRef.current;
    // 4px 미만은 클릭으로 본다(기존 동작 유지)
    if (
      Math.abs(drag.x1 - drag.x0) < DRAG_THRESHOLD &&
      Math.abs(drag.y1 - drag.y0) < DRAG_THRESHOLD
    ) {
      const picked = pickNearest(shown, plot, drag.x1, drag.y1, PICK_RADIUS, current);
      onPick(picked?.row);
      // 빈 곳을 누르면 드래그 선택도 같이 푼다
      if (!picked) onSelectRect(undefined);
      return;
    }

    const selection = rowsInRect(shown, plot, drag, undefined, current);
    onSelectRect(selection.total === 0 ? undefined : selection);
  };

  const handlePointerCancel = () => {
    dragRef.current = undefined;
    dirtyRef.current = true;
  };

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      className="size-full cursor-crosshair touch-none select-none"
      role="img"
      aria-label="끝난 요청 분포도. 가로축은 끝난 시각, 세로축은 응답시간. 아래 표에서 같은 데이터를 목록으로 본다"
    />
  );
}

/** 화면 아래에 붙는 값. 캔버스 안에 그리면 클릭 판정과 겹친다 */
export function useXViewStats(): [XViewStats, (stats: XViewStats) => void] {
  const [stats, setStats] = useState<XViewStats>({
    points: 0,
    frameMs: 0,
    maxMs: DEFAULT_MAX_MS,
  });
  const last = useRef(0);
  // 프레임마다 setState하면 그 자체가 부하다. 0.5초에 한 번만 올린다
  const report = (next: XViewStats) => {
    const now = performance.now();
    if (now - last.current < 500) return;
    last.current = now;
    setStats(next);
  };
  return [stats, report];
}
